import { FormattedMessage, useIntl } from "react-intl";

import { InvitationRead } from "@nens/rana/api-v1";
import { RanaButton, RanaForm, SubmitFunction } from "@nens/ranalib";

import { useCreateNewAccountForm } from "../hooks/use-create-new-account-form";
import { InvitationFormValues } from "../utils/validate-account-form";

interface CreateNewAccountFormProps {
  invitation: InvitationRead;
  onCancel: () => void;
  isDisabled: boolean;
  onSubmit: SubmitFunction<InvitationFormValues>;
}

export function CreateNewAccountForm({
  invitation,
  onCancel,
  isDisabled,
  onSubmit,
}: CreateNewAccountFormProps) {
  const intl = useIntl();

  const { initialValues, validateForm } = useCreateNewAccountForm(invitation);

  return (
    <RanaForm initialValues={initialValues} validate={validateForm} doSubmit={onSubmit}>
      <RanaForm.Input
        field="given_name"
        label={intl.formatMessage({ id: "form.label.first_name", defaultMessage: "First name" })}
        explanation={intl.formatMessage({
          id: "form.explanation.enter_first_name",
          defaultMessage: "Enter your first name",
        })}
        isDisabled={isDisabled}
        isRequired
      />
      <RanaForm.Input
        field="family_name"
        label={intl.formatMessage({ id: "form.label.last_name", defaultMessage: "Last name" })}
        explanation={intl.formatMessage({
          id: "form.explanation.enter_last_name",
          defaultMessage: "Enter your last name",
        })}
        isDisabled={isDisabled}
        isRequired
      />
      <RanaForm.Input
        field="email"
        type="email"
        label={intl.formatMessage({ id: "form.label.email", defaultMessage: "Email" })}
        explanation={intl.formatMessage({
          id: "form.explanation.email_immutable",
          defaultMessage: "Email cannot be changed",
        })}
        isDisabled
      />
      <RanaForm.Input
        field="username"
        label={intl.formatMessage({ id: "form.label.username", defaultMessage: "Username" })}
        explanation={intl.formatMessage({
          id: "form.explanation.enter_username",
          defaultMessage: "Enter your username",
        })}
        isDisabled={isDisabled}
        isRequired
      />
      <RanaForm.Input
        field="password"
        label={intl.formatMessage({ id: "form.label.password", defaultMessage: "Password" })}
        type="password"
        explanation={intl.formatMessage({
          id: "form.explanation.enter_password",
          defaultMessage: "Enter a strong password",
        })}
        isDisabled={isDisabled}
        isRequired
      />
      <RanaForm.Input
        field="password_again"
        label={intl.formatMessage({
          id: "form.label.password_again",
          defaultMessage: "Password again",
        })}
        type="password"
        explanation={intl.formatMessage({
          id: "form.explanation.password_again",
          defaultMessage: "Enter password again",
        })}
        isDisabled={isDisabled}
        isRequired
      />

      <RanaForm.SubmitButton isDisabled={isDisabled} w="full" my={4}>
        <FormattedMessage id="button.submit.sign_up" defaultMessage="Sign up to rana" />
      </RanaForm.SubmitButton>
      <RanaForm.Errors />
      <RanaButton variant="fourthiary" onClick={onCancel} isDisabled={isDisabled}>
        <FormattedMessage
          id="button.cancel.already_have_account"
          defaultMessage="Return to login options if you already have an account"
        />
      </RanaButton>
      <RanaForm.Errors />
    </RanaForm>
  );
}
